import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BookingService } from './booking.service';
import { FlightDataService } from './flight-data.service';
import { of } from 'rxjs';
import { UpsellService } from '../../../services/upsell/upsell.service';
import { FlightTypeEnum, Group } from '../../../api/api-checkout/models/rq-checkout-search';
import {
  ClientsPhone,
  Fee,
  Parameter,
  Passenger,
  PricingInfoDetail,
  Reservation,
} from '../../checkout-page/models';
import { buildBasePayload, buildCaller } from '../../../shared/utils';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PlatformService } from '../../../services/platform/platform.service';
import { FlightItinerary } from '../interfaces';
import { environment } from '../../../../environments/environment';
import { PassengerTypeEnum } from '../../../shared/constants';
import { VALUE_DOC_TYPES } from '../../checkout-page/constants';
import { DateUtils } from '../../../shared/utils/date-utils';
import { IUpSell } from '../../../api/api-checkout/models/rq-checkout-up-sell';

@Injectable({
  providedIn: 'root',
})
export class BookingSrvService {
  constructor(
    private readonly http: HttpClient,
    private readonly bookingService: BookingService,
    private readonly flightDataService: FlightDataService,
    private readonly upsellService: UpsellService,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly platformService: PlatformService
  ) {}

  submitBookingToSrv(formValue: any) {
    const itinerary = this.flightDataService.getValue();
    if (!itinerary || !formValue.contact?.email) return of(null);

    const device = this.platformService.getDevice();
    const browser = this.deviceDetectorService.browser;
    const caller = buildCaller(device, '', browser);

    const payload = buildBasePayload<Parameter>(
      caller,
      this.mapBookingParams(formValue, itinerary),
      this.bookingService.transactionId
    );

    return this.http.post<any>('/api/booking/srv', payload, {
      headers: { 'not-loading': 'true' },
    });
  }

  private mapBookingParams(formValue: any, itinerary: FlightItinerary) {
    const { flight } = itinerary;
    const { contact, passengers = [] } = formValue;
    const upsell = this.upsellService.getSelectedUpsell();
    const totalPay = upsell?.totalPay ?? 0;
    const validatingAirline = flight.validate?.lineaAereaValidadora ?? flight.airline.code;

    return {
      Browser: this.deviceDetectorService.browser,
      CanalVenta: '',
      Comentarios: environment.production ? '' : 'Pruebas funcionales de usuario',
      Contacto: this.mapContact(formValue),
      Cotizacion: this.mapPricing(formValue, itinerary),
      EsNegociada: false,
      GenerarPedido: false,
      IATANdcLatam: null,
      IATAResiber: null,
      IDSesionNET: '',
      IP: '192.168.1.1',
      IdCreditCard: null,
      IdDispositivo: 3,
      IdFormaPago: 0,
      IdGDS: flight.gds.idGDS,
      IdLAValidadora: validatingAirline,
      IdLAValidadoraMT: null,
      InfoBusqueda: this.mapSearchInfo(itinerary, contact),
      InsertarFormaPago: false,
      ListaPaxes: this.mapPassengers(flight, passengers),
      ListaPricingInfoDetail: this.mapPricingDetails(flight, upsell),
      ListaReservas: this.mapReservations(itinerary),
      ListaTarifa: this.mapFees(flight, upsell),
      ListaTelfsCliente: this.mapPhones(contact),
      MontoAsumeFee: 0,
      MontoTotalPagar: totalPay,
      NroCuotas: 0,
      TotalVuelo: totalPay,
    };
  }

  private mapContact(formValue: any): any {
    const {
      address = '',
      email,
      name = '',
      lastname = '',
      phoneCode,
      phoneNumber = '',
    } = formValue.contact;
    const { ruc = '', companyName = '', companyAddress = '' } = formValue.invoice ?? {};

    return {
      Address: address,
      DireccionRazonSocial: companyAddress,
      e_Mail: email,
      Id: {
        Number: '',
        Type: {
          FOID_Code: '',
          Id: 'DNI',
          LangCid: 0,
          ShortName: '',
        },
      },
      Name: name,
      Phone: {
        Area: (typeof phoneCode === 'string' ? phoneCode : phoneCode.value) ?? '',
        Number: phoneNumber ?? '',
      },
      RazonSocial: companyName,
      RUC: ruc,
      SurName: lastname,
      TypeVoucher: '01',
    };
  }

  private mapPricing(formValue: any, itinerary: FlightItinerary) {
    const { discountAmount = 0 } = this.bookingService;
    const { passengers = [] } = formValue;
    const { params } = itinerary;

    const flightType: string = this.mapFlightType(params.flightType);

    const countByType = (type: PassengerTypeEnum) =>
      passengers.filter((p: any) => p?.type === type).length;

    return {
      TipoVuelo: flightType,
      CantAdt: countByType(PassengerTypeEnum.ADULT),
      CantChd: countByType(PassengerTypeEnum.CHILD),
      MontoDscto: discountAmount,
    };
  }

  private mapSearchInfo(itinerary: FlightItinerary, contact: any) {
    const {
      params,
      flight: {
        gds: { officeId, origenSearch = '', pcc = '' },
        detailPricing,
      },
    } = itinerary;

    const departureDate = this.getFormattedDepartureDate(itinerary.params);
    const returnDate = this.getFormattedReturnDate(itinerary.params);

    const emailUsuario = params.email?.trim() || contact.email;
    const montoTotal = detailPricing?.totalPay ?? 0;

    return {
      AccountCode: null,
      ClaseCabina: '',
      EmailUsuario: emailUsuario,
      FechaRetorno: returnDate,
      FechaSalida: departureDate,
      IdCliente: contact.idLoggin?.toString() ?? '1',
      IdLang: 1,
      IdLogSearch: 0,
      IdLogSearchIS: 0,
      IdOficina: officeId ?? null,
      IdUsuario: null,
      IdWeb: 7,
      MontoTotalPagar: montoTotal,
      Orientacion: 0,
      OrigenSearch: origenSearch,
      PCC: pcc,
      Pcc_Kiu: '',
      PNRAerolinea: '',
      PNRNdc: null,
      PNRSabre: '',
      RUC: '',
      RutaBusqueda: '',
      Status: 1,
      TimeLimit: new Date().toISOString().split('.')[0].replace('T', ' '),
      TotalVuelo: montoTotal,
    };
  }

  private mapPassengers(flight: Group, passengers: any[]): Passenger[] {
    const fareBreakdowns = flight.pricingInfo.itinTotalFare.fareBreakDowns;

    try {
      return passengers.map((pax, index) => {
        const fare = fareBreakdowns.find((item) => item.passengerType.equivalentCode === pax.type)!;

        return {
          Apellidos: pax.lastname,
          Asiento: '',
          BaseFare: fare.passengerFare.baseFare.toString(),
          FareType: fare.passengerType.code,
          FecNac: DateUtils.formatFromDayMonthYear({
            day: pax.day,
            month: pax.month,
            year: pax.year,
          }),
          FOID: 'NO_ESP',
          Gender: pax.gender,
          IdPax: `${index + 1}`,
          MontoFee: fare.passengerFare.feeNMV.toString(),
          MontoFeePTA: fare.passengerFare.feePTA.toString(),
          Nacionalidad: pax.nationality.name,
          Nombre: pax.name,
          NrosPaxFrecuente: null,
          NumeroDocumento: pax.documentNumber,
          PNR: '',
          Tipo: pax.type,
          TipoDocumento: VALUE_DOC_TYPES[Number(pax.documentType) as 0 | 1 | 2],
          TipoPrivate: pax.type,
          TotalTaxes: fare.passengerFare.taxes.toString(),
        };
      });
    } catch (_) {
      return [];
    }
  }

  private mapPricingDetails(flight: Group, upsell: IUpSell | null): PricingInfoDetail[] {
    const isUpsell = !!upsell?.fareBreakDowns;
    const fareBreakdowns = isUpsell
      ? upsell.fareBreakDowns
      : flight.pricingInfo.itinTotalFare.fareBreakDowns;

    const includesHandBag = upsell?.includeHandBagDep ?? false;
    const includesHoldBag = upsell?.includesHoldBagDep ?? false;

    const calculateCantidadMaletas = (equipajes: { piezas: number }[] = []): number =>
      equipajes.reduce((sum, e) => sum + e.piezas, 0);

    return fareBreakdowns.map((fare: any, index): PricingInfoDetail => {
      const equipaje = fare.equipaje?.equipaje ?? [];

      return {
        CantidadMaletas: calculateCantidadMaletas(equipaje),
        DescripcionEquipajePersonal: '',
        DescripcionMedidas: '',
        DescripcionPeso: '',
        IdSegmento: index + 1,
        MedidaPermitida: '',
        PermiteEquipajeMano: '1',
        PermiteEquipajePersonal: isUpsell && includesHandBag ? '1' : '0',
        PermiteMaleta: isUpsell && includesHoldBag ? '1' : '0',
        PesoPermitido: '',
        Reserva_Id: 0,
        Tarifa: isUpsell ? fare.totalFare : fare.passengerFare.totalFare,
        TipoPax: isUpsell ? fare.passengerType : fare.passengerType.code,
      };
    });
  }

  private mapReservations(itinerary: FlightItinerary): Reservation[] {
    const {
      params,
      flight: { departure, returns },
    } = itinerary;

    const flightType = this.mapFlightType(params.flightType);
    const returnSegments = returns?.segments?.[0]?.flightSegments ?? [];

    const buildSegment = (
      segment: any,
      index: number,
      isReturn: boolean,
      countryInfo: { destinationCountry: string; originCountry: string }
    ) => ({
      IdSegmento: `${index + 1}`,
      Aerolinea: segment.marketingAirline.code,
      Cabina: segment.cabin,
      CiudadLlegada: segment.arrivalAirport.code,
      CiudadSalida: segment.departureAirport.code,
      ElapsedTime: segment.elapsedTime,
      EsRetorno: isReturn ? '1' : '0',
      FareBasis: segment.fareBasis,
      FechaLlegada: segment.arrivalDateTime.replace('T', ' '),
      FechaSalida: segment.departureDateTime.replace('T', ' '),
      Guarantee: segment.guarantee ? '1' : '0',
      IdLAOperador: segment.operatingAirline.code,
      PaisLlegada: countryInfo.destinationCountry,
      PaisSalida: countryInfo.originCountry,
      PNR: '',
      VueloNumero: segment.flightNumber.toString(),
    });

    const mapSegments = (
      segments: any[],
      isReturn: boolean,
      defaultCountryInfo: { destinationCountry: string; originCountry: string }
    ) =>
      segments.map((segment, index) => buildSegment(segment, index, isReturn, defaultCountryInfo));

    const reservations: Reservation[] = departure.map((dep, i) => {
      const flightSegments = dep.segments?.[0]?.flightSegments ?? [];
      const isLastOneWay =
        flightType !== 'OW' && returnSegments.length === 0 && i === departure.length - 1;

      return {
        IdReserva: `${i + 1}`,
        PNRGDS: '',
        PNRAIR: '',
        ListaSegmentos: mapSegments(flightSegments, isLastOneWay, {
          destinationCountry: dep.destinationCity.country,
          originCountry: dep.originCity.country,
        }),
      };
    });

    if (returnSegments.length > 0)
      reservations.push({
        IdReserva: `${reservations.length + 1}`,
        PNRGDS: '',
        PNRAIR: '',
        ListaSegmentos: mapSegments(returnSegments, true, {
          destinationCountry: returns?.destinationCity?.country ?? '',
          originCountry: returns?.originCity?.country ?? '',
        }),
      });

    return reservations;
  }

  private mapFees(flight: Group, upsell: IUpSell | null): Fee[] {
    const isUpsell = !!upsell?.fareBreakDowns;
    const fareBreakdowns = isUpsell
      ? upsell!.fareBreakDowns
      : flight.pricingInfo.itinTotalFare.fareBreakDowns;

    const calculateTotalTaxes = (taxes: { fareAmount: number }[] = []): number =>
      taxes.reduce((total, { fareAmount }) => total + fareAmount, 0);

    const extractFareData = (fare: any): Fee => {
      const baseFare = isUpsell ? fare.baseFare : fare.passengerFare.baseFare;
      const quantity = isUpsell ? fare.quantity : fare.passengerType.quantity;
      const totalFare = isUpsell ? fare.totalFare : fare.passengerFare.totalFare;
      const totalTaxes = isUpsell ? calculateTotalTaxes(fare.taxes) : fare.passengerFare.taxes;
      const passengerType = isUpsell ? fare.passengerType : fare.passengerType.code;

      return {
        BaseFare: `${baseFare}`,
        PassengerQuantity: `${quantity}`,
        PassengerType: passengerType,
        TotalFare: `${totalFare}`,
        TotalTaxes: `${totalTaxes}`,
      };
    };

    return fareBreakdowns.map(extractFareData);
  }

  private mapPhones(contact: any): ClientsPhone[] {
    const phone: ClientsPhone = {
      CodPaisTelf:
        typeof contact.phoneCode === 'string' ? contact.phoneCode : contact.phoneCode.value,
      CodAreaTelf: 1,
      NumTelf: contact.phoneNumber,
      IdTipoTelf: 2,
      IdSecuencia: 1,
      IdReserva: 0,
    };
    return Array(phone);
  }

  private mapFlightType(type: FlightTypeEnum): string {
    return (
      {
        [FlightTypeEnum.RoundTrip]: 'RT',
        [FlightTypeEnum.OneWay]: 'OW',
        [FlightTypeEnum.MultiCity]: 'MT',
      }[type] ?? ''
    );
  }

  private getFormattedDepartureDate(params: any): string {
    const date =
      params.flightType === FlightTypeEnum.MultiCity
        ? params.multicity?.[0]?.departureDate
        : params.departureDate;

    return date ? `${date}T00:00:00` : '';
  }

  private getFormattedReturnDate(params: any): string {
    if (params.flightType === FlightTypeEnum.RoundTrip) {
      return `${params.arrivalDate}T00:00:00`;
    } else if (params.flightType === FlightTypeEnum.MultiCity) {
      const lastSegment = params.multicity[params.multicity.length - 1];
      return `${lastSegment?.departureDate ?? ''}T00:00:00`;
    }
    return '';
  }
}
